'use client'

import { useState, useEffect, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'

interface CookiePreferences {
  essential: boolean
  analytics: boolean
  functional: boolean
  marketing: boolean
}

const CONSENT_COOKIE_NAME = 'sc_cookie_consent'
const CONSENT_COOKIE_MAX_AGE = 365 * 24 * 60 * 60 // 1 year

function readConsentCookie(): CookiePreferences | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]+)`))
    if (!match) return null
    return JSON.parse(decodeURIComponent(match[1])) as CookiePreferences
  } catch {
    return null
  }
}

function writeConsentCookie(prefs: CookiePreferences): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(prefs))}; Max-Age=${CONSENT_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`
  } catch {
    // ignore
  }
}

// Pages where the cookie banner should not appear (auth flows, etc.)
const EXCLUDED_PATHS = ['/auth/login', '/auth/mfa', '/auth/verify', '/rate-limited', '/client-zone']

export default function CookieConsent() {
  const pathname = usePathname()
  const [mounted, setMounted] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    functional: false,
    marketing: false
  })

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true))
  }, [])

  const isExcludedPath = EXCLUDED_PATHS.some(p => pathname.startsWith(p))
  const consent = useMemo(
    () => mounted && !isExcludedPath ? readConsentCookie() : null,
    [mounted, isExcludedPath],
  )
  const showBanner = mounted && !isExcludedPath && !consent && !dismissed

  useEffect(() => {
    if (consent) {
      requestAnimationFrame(() => setPreferences(consent))
    }
  }, [consent])

  const initializeTracking = (prefs: CookiePreferences) => {
    // Initialize Google Analytics if analytics cookies are accepted
    if (prefs.analytics && typeof window !== 'undefined') {
      // Dispatch custom event to trigger Google Analytics loading
      window.dispatchEvent(new CustomEvent('cookie-consent-changed', {
        detail: { analytics: true }
      }))
      console.log('Analytics cookies accepted - initializing tracking')
    }

    // Initialize other tracking scripts based on preferences
    if (prefs.marketing && typeof window !== 'undefined') {
      console.log('Marketing cookies accepted - initializing marketing tracking')
      // Add your marketing tracking scripts here
    }

    // Initialize functional cookies
    if (prefs.functional && typeof window !== 'undefined') {
      console.log('Functional cookies accepted')
      // Add your functional tracking scripts here
    }
  }

  const acceptAllCookies = () => {
    const allPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true
    }
    setPreferences(allPreferences)
    writeConsentCookie(allPreferences)
    setDismissed(true)
    setShowPreferences(false)
    initializeTracking(allPreferences)
  }

  const acceptEssentialOnly = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false
    }
    setPreferences(essentialOnly)
    writeConsentCookie(essentialOnly)
    setDismissed(true)
    setShowPreferences(false)
  }

  const savePreferences = () => {
    writeConsentCookie(preferences)
    setDismissed(true)
    setShowPreferences(false)
    initializeTracking(preferences)
  }

  const updatePreference = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'essential') return // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }))
  }

  if (!showBanner) return null

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-6 z-50 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">🍪 We use cookies</h3>
              <p className="text-gray-300 text-sm">
                We use cookies to enhance your browsing experience, serve personalized content,
                and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Manage Preferences
              </button>
              <button
                onClick={acceptEssentialOnly}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Essential Only
              </button>
              <button
                onClick={acceptAllCookies}
                className="px-6 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-400">
            <Link href="/cookie-policy" className="hover:text-white underline">
              View our Cookie Policy
            </Link>
            {' | '}
            <Link href="/legal" className="hover:text-white underline">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Essential Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Essential Cookies</h3>
                    <input
                      type="checkbox"
                      checked={preferences.essential}
                      disabled
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                    />
                  </div>
                  <p className="text-gray-600 text-sm">
                    These cookies are necessary for the website to function and cannot be switched off in our systems.
                    They are usually only set in response to actions made by you which amount to a request for services.
                  </p>
                  <div className="mt-2 text-xs text-gray-500">
                    Always active • Cannot be disabled
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Analytics Cookies</h3>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => updatePreference('analytics', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-gray-600 text-sm">
                    These cookies allow us to count visits and traffic sources so we can measure and improve
                    the performance of our site. They help us to know which pages are the most and least popular.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Functional Cookies</h3>
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => updatePreference('functional', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-gray-600 text-sm">
                    These cookies enable the website to provide enhanced functionality and personalization.
                    They may be set by us or by third party providers whose services we have added to our pages.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Marketing Cookies</h3>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => updatePreference('marketing', e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-gray-600 text-sm">
                    These cookies may be set through our site by our advertising partners. They may be used by those
                    companies to build a profile of your interests and show you relevant adverts.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
                <button
                  onClick={acceptEssentialOnly}
                  className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded transition-colors"
                >
                  Essential Only
                </button>
                <button
                  onClick={savePreferences}
                  className="px-6 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
